import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DirectSubscriptionService } from '@app/modules/direct/services/direct-subscription.service';
import { map } from 'rxjs/operators';
import {
  CancellationPolicy,
  CancellationTerm,
  CancellationTermType,
  DirectService,
  PaymentTermStatus,
} from '../../services/direct.service';

@Component({
  standalone: false,
  templateUrl: './direct-edit-booking-policy.component.html',
  styleUrls: ['./direct-edit-booking-policy.component.scss'],
})
export class DirectEditBookingPolicyComponent implements OnInit {
  CancellationTermType = CancellationTermType;

  mode: 'new' | 'edit' = 'new';
  policy: CancellationPolicy;

  saving = false;
  recentlySaved = false;
  origDefault = false;

  PaymentTermStatus = PaymentTermStatus;

  defaultPaymentTerms: CancellationTerm[] = [
    {
      enabled: true,
      type: CancellationTermType.PaymentTermsAfterAccepted,
      label: '20% refund until 20 days after booking accepted',
      percentage: 20,
      trigger_time: {
        amount: 0,
        units: 'days',
      },
      restriction: null,
    },
    {
      enabled: true,
      type: CancellationTermType.PaymentTermsBeforeCheckin,
      label: '30% due 45 days before check-in',
      percentage: 30,
      trigger_time: {
        amount: 45,
        units: 'days',
      },
      restriction: null,
    },
    {
      enabled: true,
      type: CancellationTermType.PaymentTermsBeforeCheckin,
      label: '50% due 10 days before check-in',
      percentage: 50,
      trigger_time: {
        amount: 10,
        units: 'days',
      },
      restriction: null,
    },
  ];

  isPremium$ = this.directSubsriptionService.isPremium$;

  constructor(
    private route: ActivatedRoute,
    private direct: DirectService,
    private router: Router,
    private directSubsriptionService: DirectSubscriptionService
  ) { }

  ngOnInit(): void {
    this.setModeFromRoute();
  }

  setModeFromRoute(): void {
    this.route.params
      .pipe(
        map((params) => {
          this.mode = params.id ? 'edit' : 'new';

          if (this.mode === 'new') {
            this.policy = this.createDefaultModeForNew();
          } else {
            this.direct.fetchCancellationPolicy(params.id).subscribe((policy) => {
              this.policy = policy;
              this.origDefault = policy.default;
            });
          }
        })
      )
      .subscribe();
  }

  goBack() {
    this.router.navigate(['/direct/booking-policies']);
  }

  saveChanges() {
    if (this.mode === 'new') {
      this.createPolicy();
    } else {
      this.updatePolicy();
    }
  }

  createPolicy() {
    this.direct.createNewCancellationPolicy(this.policy).subscribe((res) => {
      if (res?.data?.uuid) {
        this.router.navigate(['/direct/booking-policies', res.data.uuid]);
      }
    });
  }

  updatePolicy() {
    this.saving = true;

    this.direct.updateCancellationPolicy(this.policy).subscribe(
      (data) => {
        this.saving = false;
        this.recentlySaved = true;

        this.policy = {
          ...this.policy,
          terms: [...this.direct.sortCancellationTerms(this.policy.terms)],
        };

        setTimeout(() => {
          this.recentlySaved = false;
        }, 2000);
      },
      () => {
        this.saving = false;
      }
    );
  }

  createDefaultModeForNew(): CancellationPolicy {
    return {
      uuid: 'new',
      name: 'New booking policy',
      default: false,
      terms: [
        {
          enabled: true,
          type: CancellationTermType.AfterBooking,
          percentage: 100,
          label: '100% refund until 24 hours after booking accepted',
          trigger_time: {
            amount: 24,
            units: 'hours',
          },
          days_before_checkin_restriction: {
            amount: 7,
            units: 'days',
          },
          is_long_stay: false,
        },
      ],
      properties: [],
      payment_terms_status: PaymentTermStatus.Aligned,
      long_stay_days: null,
      grace_period_in_hours: 48,
      host_only_service_fee_enabled: false,
    };
  }

  addTerm(type: CancellationTermType, policy: CancellationPolicy) {
    if (
      type === CancellationTermType.PaymentTermsAfterAccepted ||
      type === CancellationTermType.PaymentTermsBeforeCheckin
    ) {
      this.addPaymentTerm(type, policy);
      return;
    }

    this.addCancellationTerm(type, policy);
  }

  addCancellationTerm(type: CancellationTermType, policy: CancellationPolicy): void {
    const newTerm: CancellationTerm = {
      enabled: true,
      type: type,
      percentage: 50,
      label: '50% refund until 7 days before check-in',
      trigger_time: {
        amount: 7,
        units: 'days',
      },
      days_before_checkin_restriction: null,
      is_long_stay: false,
    };

    this.policy = {
      ...policy,
      terms: [...policy.terms, newTerm],
    };
  }

  addPaymentTerm(type: CancellationTermType, policy: CancellationPolicy): void {
    const paymentTerms = this.getAllPaymentTerms();

    if (paymentTerms.length < this.defaultPaymentTerms.length) {
      const currentTotalPercentage: number = paymentTerms.reduce((acc, term) => acc + term.percentage, 0);
      const percentageLeft = 100 - currentTotalPercentage;
      const matchingDefaultPaymentTerm = this.defaultPaymentTerms[paymentTerms.length];

      const percentage =
        matchingDefaultPaymentTerm.percentage <= percentageLeft
          ? matchingDefaultPaymentTerm.percentage
          : percentageLeft;
      const triggerAmount = matchingDefaultPaymentTerm.trigger_time.amount;

      const newTerm: CancellationTerm = {
        enabled: true,
        type: CancellationTermType.PaymentTermsBeforeCheckin,
        percentage: percentage,
        label: percentage + '% refund until ' + triggerAmount + ' days before check-in',
        trigger_time: { amount: triggerAmount, units: 'days' },
        restriction: null,
        is_long_stay: false,
      };

      this.policy = {
        ...policy,
        terms: [...policy.terms, ...[newTerm]],
      };
    }
  }

  deleteTerm(term: CancellationTerm) {
    this.policy = {
      ...this.policy,
      terms: this.policy.terms.filter((t) => t !== term),
    };
  }

  deletePolicy() {
    this.direct.deleteCancellationPolicy(this.policy.uuid).subscribe((res) => {
      if (res) {
        this.router.navigate(['/direct/booking-policies']);
      }
    });
  }

  alignedWithPaymentTermsChanged() {
    if (!this.policy.terms.some((term) => term.type === CancellationTermType.PaymentTermsAfterAccepted)) {
      this.addDefaultPaymentTerms();
    }
  }

  addDefaultPaymentTerms() {
    this.policy = {
      ...this.policy,
      terms: [...this.policy.terms, ...this.defaultPaymentTerms],
    };
  }

  getAllPaymentTerms(): CancellationTerm[] {
    if (this.policy && this.policy.terms) {
      return this.policy.terms.filter(
        (term) =>
          term.type === CancellationTermType.PaymentTermsAfterAccepted ||
          term.type === CancellationTermType.PaymentTermsBeforeCheckin
      );
    }

    return [];
  }

  getAllCancellationTerms(): CancellationTerm[] {
    if (this.policy && this.policy.terms) {
      return this.policy.terms.filter(
        (term) =>
          term.type === CancellationTermType.AfterBooking ||
          term.type === CancellationTermType.BeforeCheckIn ||
          term.type === CancellationTermType.UntilCheckIn
      );
    }

    return [];
  }
}
